/**
 * RESPONSIBILITY: answer whether a carousel is allowed to move right now.
 *
 * Its own module because it is the one part that is about TIME rather than about cards,
 * and because the rule it holds is the part worth reading on its own.
 *
 * FOCUS IS THE SIGNAL, not the pointer. A carousel that stops whenever a mouse passes
 * over it stops for people who are not using it at all, and on a wide page that is most
 * of the time the pointer is anywhere near the middle of the screen. Focus says somebody
 * has actually taken hold of a control, and losing it says they have finished — so that
 * is decided by the component, and what is left here is the case focus cannot see: a
 * swipe, or a press in a browser that does not focus a button when it is clicked.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/** Somebody who has asked their system not to animate things. Asked fresh each time. */
export const reducedMotion = (): boolean =>
  typeof window !== 'undefined'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export interface Idle {
  /** True once nothing has happened for the quiet period. */
  idle: boolean;
  /** Call on a press or a swipe. Holds the carousel for the quiet period. */
  touched: () => void;
  /** Call when the hold should end NOW — focus left, so there is nothing to wait for. */
  settle: () => void;
}

export function useIdle(seconds: number | undefined): Idle {
  // Zero is a real answer — "start again straight away" — so only an absent or absurd
  // value falls back.
  const wait = Math.max(0, Math.round(seconds ?? 8)) * 1000;
  const [idle, setIdle] = useState(true);
  const timer = useRef<number>();

  const settle = useCallback(() => {
    window.clearTimeout(timer.current);
    setIdle(true);
  }, []);

  const touched = useCallback(() => {
    setIdle(false);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setIdle(true), wait);
  }, [wait]);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  return { idle, touched, settle };
}
